import React, { useState, useEffect, useRef } from "react";
import { InputAdornment, TextField } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faTimes } from "@fortawesome/free-solid-svg-icons";
import "./Search.css";
import { customSx } from "../createSecondaryOrder/util";

interface SearchProps<T> {
  searchFunction: (value: string) => void;
  label?: string;
  resetSearch?: boolean;
  value?: string;
  insideCreateSecondaryOrder?: boolean;
}

const Search = <T extends Record<string, any>>({
  searchFunction,
  label = "Search",
  resetSearch = false,
  value = "",
  insideCreateSecondaryOrder = false,
}: SearchProps<T>) => {
  const [searchValue, setSearchValue] = useState(value);
  const [debouncedValue, setDebouncedValue] = useState(searchValue);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClear = () => {
    setSearchValue("");
    setDebouncedValue("");
    searchFunction("");
  };

  // Focus on Ctrl+F or Cmd+F
  useEffect(() => {
    if ( insideCreateSecondaryOrder ) inputRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => { 
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a' && searchValue.trim()) {
        const activeElement = document.activeElement;
        const isSearchFocused = activeElement === inputRef.current;
        
        if (isSearchFocused || searchValue.trim()) {
          event.stopPropagation();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [searchValue]);

  // Reset search value when resetSearch changes
  useEffect(() => {
    if (resetSearch) {
      handleClear();
    }
  }, [resetSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(searchValue);
    }, 400);

    return () => {
      clearTimeout(timer); 
    };
  }, [searchValue]);

  useEffect(() => {
    setSearchValue(value);
    setDebouncedValue(value);
  }, [value]);

  useEffect(() => {
    searchFunction(debouncedValue);
  }, [debouncedValue, searchFunction]);

  return (
    <TextField
      id="outlined-basic"
      label={label}
      variant="outlined"
      size="small"
      fullWidth
      autoComplete="off"
      value={searchValue}
      onChange={(e) => setSearchValue(e.target.value)}
      sx={customSx}
      inputRef={inputRef}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <div className="Search_wrapper">
              {searchValue ? (
                <FontAwesomeIcon
                  icon={faTimes}
                  onClick={handleClear}
                  style={{ cursor: "pointer" }}
                />
              ) : (
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              )}
            </div>
          </InputAdornment>
        ),
      }}
    />
  );
};

export default Search;
